<script setup>
import { computed, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useConnectionStore } from '@/stores/connection'
import Loading from 'vue-loading-overlay'
import 'vue-loading-overlay/dist/css/index.css'
import { useTextareaAutosize } from '@vueuse/core'
import MessagesComponent from '@/components/MessagesComponent.vue'
import NewMessageComponent from '@/components/NewMessageComponent.vue'
import assistantApi from '@/api/assistant'
import { useUsersStore } from '@/stores/users'
import { useMessagesStore } from '@/stores/messages'
import { onBeforeNavigation } from '@/utils/onBeforeNavigation'
import { useI18n } from "vue-i18n";
import axios from '@/api/axiosInstance.js'
import { useCaptcha } from '@/composables/useCaptcha';
import { useCaptchaStore } from '@/stores/captcha';

const { t } = useI18n();
const thread = ref('')
const usersStore = useUsersStore()
const messagesStore = useMessagesStore()
const connectionStore = useConnectionStore()

const router = useRouter()
const route = useRoute() // We'll use this to get the language parameter
const showLoader = ref(false)
const { textarea, input: input_message } = useTextareaAutosize()

const captchaProvider = import.meta.env.VITE_APP_CAPTCHA_PROVIDER || 'recaptcha';
const isTurnstile = computed(() => captchaProvider === 'turnstile');

const { getCaptchaToken } = useCaptcha();

const captchaStore = useCaptchaStore();

function validateInput() {

  if (!input_message.value || input_message.value?.length === 0) {
    return false;
  }

  const wordPattern =  /\p{L}+/u;
  return wordPattern.test(input_message.value);
}

 onMounted(async () => {
   if (!localStorage.getItem("uuid")) {
     const { data } = await axios.post("/user/create_uuid")
     localStorage.setItem('uuid', data.id)
   }
   usersStore.setCurrentUser({
      id: localStorage.getItem("uuid"),
    })

  let { threadId } = route.params
  threadId = threadId === undefined ? 'new' : threadId
  await getHistory(threadId)
  if (usersStore.getCurrentUserId && threadId) {
    connectionStore.subscribe(usersStore.getCurrentUserId.toString(), threadId)
    messagesStore.bindEvents()
  }

})

onBeforeNavigation((to, from, next) => {
  // This check is updated to be more generic and work with language prefixes
  if (to.path.includes('mne/chats') || (to.params.lang && Object.keys(to.params).length === 1)) {
    input_message.value = []
    thread.value = ''
    messagesStore.setMessageLoading(false)
    let { threadId } = to.params
    threadId = threadId === undefined ? 'new' : threadId
    thread.value = threadId
    if (to.query?.history !== '0') {
      getHistory(threadId)
      if (usersStore.getCurrentUserId && threadId) {
        connectionStore.subscribe(
          usersStore.getCurrentUserId.toString(),
          threadId,
        )
        messagesStore.bindEvents()
      }
    }
    next()
  }
})

async function getHistory(threadId) {
  messagesStore.setMessages([])
  thread.value = threadId
  if (threadId && threadId !== 'new') {
    showLoader.value = true
    messagesStore.setMessageLoading(true)
    assistantApi
      .getThreadHistory(threadId)
      .then((response) => {
        if(response.data === null) {
          // MODIFIED: Added language prefix to the path
          router.push(`/${route.params.lang}/chats/new`)
        }else{
          messagesStore.setMessages(response.data)
        }

        messagesStore.setMessageLoading(false)
        showLoader.value = false
      })
      .catch((error) => {
        console.log(error)
        // MODIFIED: Added language prefix to the path
        router.push(`/${route.params.lang}/chats/new`)
        messagesStore.setMessageLoading(false)
        showLoader.value = false
      })
  }
}

async function sendMessage(e) {
  e.preventDefault()
  if (!input_message.value || messagesStore.getMessageLoading) {
    return
  }

  const messageContent = input_message.value;
  input_message.value = '';

  messagesStore.setMessageLoading(true)
  const new_user_message = {
    role: 'user',
    content: messageContent,
  }
  const new_assistant_message = {
    role: 'assistant',
    content: 'Loading...',
  }
  messagesStore.setMessages([
    ...messagesStore.getAllMessages,
    new_user_message,
    new_assistant_message,
  ])
  const token = await getCaptchaToken("sendMessage");
  assistantApi
    .getAnswer(messageContent, thread.value, token)
    .then(async (response) => {
      const { thread_id, all_messages } = response.data
      const newMessage = all_messages[all_messages.length - 1]
      messagesStore.replaceLastMessage(newMessage)
      messagesStore.setMessageLoading(false)
      if (!thread.value || thread.value === 'new') {
        messagesStore.setMessageLoading(true)
        await usersStore.setCurrentUserThreads()
        // MODIFIED: Added language prefix to the path
        await router.push(`/${route.params.lang}/chats/${thread_id}?history=0`)
        connectionStore.subscribe(
          usersStore?.getCurrentUserId.toString(),
          thread_id,
        )
        messagesStore.bindEvents()
      }
    })
    .catch((error) => {
      messagesStore.setMessageLoading(false)
      console.log(error)
      if (error.request.status === 429) {
        messagesStore.replaceLastMessage({
          role: 'assistant',
          content:
            'Poštovani, prekoračili ste dozvoljeni limit dnevnih poruka. Ako mislite da je ovo greška kontaktirajte help@airmontenegro.com.',
          createdAt: Math.floor(Date.now() / 1000),
          message_id: 'msg_id',
          sentiment: null,
        })
      }
    })
  input_message.value = ''
  if (isTurnstile.value) {
    captchaStore.resetToken();
    window.dispatchEvent(new Event('reset-turnstile'));
  }
}

async function sendTemplateMessage(message) {
  messagesStore.setMessageLoading(true)
  const new_user_message = {
    role: 'user',
    content: message,
  }
  const new_assistant_message = {
    role: 'assistant',
    content: 'Loading...',
  }
  messagesStore.setMessages([
    ...messagesStore.getAllMessages,
    new_user_message,
    new_assistant_message,
  ])
  const token = await getCaptchaToken("click");
  assistantApi
    .getAnswer(message, thread.value, token)
    .then(async (response) => {
      const { thread_id, all_messages } = response.data
      const newMessage = all_messages[all_messages.length - 1]
      messagesStore.replaceLastMessage(newMessage)
      messagesStore.setMessageLoading(false)
      if (!thread.value || thread.value === 'new') {
        messagesStore.setMessageLoading(true)
        await usersStore.setCurrentUserThreads()
        // MODIFIED: Added language prefix to the path
        await router.push(`/${route.params.lang}/chats/${thread_id}?history=0`)
        connectionStore.subscribe(
          usersStore.getCurrentUserId.toString(),
          thread_id,
        )
        messagesStore.bindEvents()
      }
    })
    .catch((error) => {
      console.log(error)
      messagesStore.setMessageLoading(false)
      if (error.request.status === 429) {
        messagesStore.replaceLastMessage({
          role: 'assistant',
          content:
            'Poštovani, prekoračili ste dozvoljeni limit dnevnih poruka. Ako mislite da je ovo greška kontaktirajte help@airmontenegro.com.',
          createdAt: Math.floor(Date.now() / 1000),
          message_id: 'msg_id',
          sentiment: null,
        })
      }
    })
  if (isTurnstile.value) {
    captchaStore.resetToken();
    window.dispatchEvent(new Event('reset-turnstile'));
  }
}

const sendFeedback = async (message, sentiment) => {
  let comment = null
  sentiment = message.sentiment === sentiment ? 0 : sentiment
  if (sentiment === -1) {
    comment = sentiment === 0 ? null : 'Neki komentar'
  }
  messagesStore.setMessageFeedback(message, sentiment)
  const token = await getCaptchaToken("submit");

  assistantApi
    .sendFeedback({
      message_id: message.message_id,
      sentiment: sentiment,
      comment: comment,
    }, token)
    .catch((error) => {
      console.log(error)
    })
  if (isTurnstile.value) {
    captchaStore.resetToken();
    window.dispatchEvent(new Event('reset-turnstile'));
  }
}
</script>

<template>
  <div
    class="relative mx-auto flex h-screen w-full flex-col rounded-[20px]"
  >
    <Loading
      v-model:active="showLoader"
      background-color="transparent"
      :is-full-page="false"
    />
    <div
      id="MsgHistory"
      ref="MsgHistory"
      class="flex-1 overflow-auto"
    >
      <NewMessageComponent
        v-if="
          thread === 'new' &&
          messagesStore.getAllMessages.length === 0 &&
          !showLoader
        "
        @template-prompt-clicked="sendTemplateMessage"
      />
      <MessagesComponent
        v-else
        :messages="messagesStore.getAllMessages"
        :send-feedback="sendFeedback"
      />
    </div>
    <div class="mx-auto w-full max-w-3xl px-4 lg:px-0">
      <form
        action="#"
        class="relative flex w-full items-end justify-center rounded-[12px] bg-white border border-gray-200 p-2"
      >
        <textarea
          id="input_message"
          ref="textarea"
          v-model="input_message"
          name="input_message"
          class="mr-[70px] block max-h-[232px] w-full resize-none border-0 bg-white text-gray-darker placeholder:text-gray-500 focus:border-0 focus:outline-0 focus:ring-0 focus:ring-offset-0 sm:text-sm sm:leading-6"
          :placeholder="t('common.messagePlaceholder')"
          @keydown.enter.exact="sendMessage"
        />
        <button
          type="submit"
          :disabled="messagesStore.getMessageLoading || !validateInput()"
          :class="[
            'absolute right-3 flex items-center justify-center rounded-full disabled:bg-primary-light p-2 text-sm font-semibold text-white shadow-sm',
            messagesStore.getMessageLoading
              ? 'cursor-not-allowed bg-primary-light hover:bg-primary-light'
              : 'bg-primary hover:bg-primary-light',
          ]"
          @click="sendMessage"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="currentColor"
            class="h-6 w-6"
          >
            <path
              d="M3.478 2.404a.75.75 0 0 0-.926.941l2.432 7.905H13.5a.75.75 0 0 1 0 1.5H4.984l-2.432 7.905a.75.75 0 0 0 .926.94 60.519 60.519 0 0 0 18.445-8.986.75.75 0 0 0 0-1.218A60.517 60.517 0 0 0 3.478 2.404Z"
            />
          </svg>
        </button>
      </form>
      <div class="my-2 flex items-center justify-center gap-x-1.5 text-center">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          stroke-width="1.5"
          stroke="currentColor"
          class="ml-2 h-6 w-6 text-gray lg:ml-0 lg:h-5 lg:w-5"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            d="m11.25 11.25.041-.02a.75.75 0 0 1 1.063.852l-.708 2.836a.75.75 0 0 0 1.063.853l.041-.021M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9-3.75h.008v.008H12V8.25Z"
          />
        </svg>

        <span class="text-start text-xs font-light text-gray md:hidden">
          {{ t('common.warningMessage')}}
        </span>
        <span class="hidden text-start text-xs font-light text-gray md:block">
           {{ t('common.warningMessage')}}
        </span>
      </div>
    </div>
  </div>
</template>